// @flow
import { get, find, nth, isEmpty } from 'lodash';
import moment from 'moment';
import { momentFromApiDate } from 'Utils/date/apiDate';

import { ACCOMMODATION_COMFORT_ORDER } from 'Utils/accommodation/accommodation.constants';
import { LEGAL_INFO_WEBTHEO_ID, LEGAL_INFO_THEO_ID } from 'Utils/booking-process/constants';
import { pluralizeLabel } from 'Utils/label/labelUtils';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import {
  PACKAGE_ROOM_ONLY,
  PACKAGE_ALL_INCLUSIVE,
  CEFALU_RESORT,
  DELUXE_COMFORT,
  PRICE_COUNTER_PROPOSAL_THRESHOLD,
  COMFORT_TYPE_SUITE,
  COMFORT_TYPE_CHALET,
  COMFORT_TYPE_VILLA,
} from './constants';

const OPTIONS_DAYS_RANGE = 29;
const DEFAULT_STOCK_WARNING_LIMIT = 2;

function getAccommodationComfortIndex(accommodation: Accommodation): number {
  return ACCOMMODATION_COMFORT_ORDER.indexOf(get(accommodation, 'categoryId'));
}

function compareProposalsByComfort(proposal1: Proposal, proposal2: Proposal): number {
  const proposal1Index = getAccommodationComfortIndex(get(proposal1, 'accommodations[0]'));
  const proposal2Index = getAccommodationComfortIndex(get(proposal2, 'accommodations[0]'));

  // if we have not sorted the accommodation we don't change the order returned by the api
  if (proposal1Index === -1 || proposal2Index === -1) {
    return 0;
  }

  return proposal1Index - proposal2Index;
}

function compareAccommodationsByComfort(
  accommodation1: Accommodation,
  accommodation2: Accommodation,
): number {
  return (
    getAccommodationComfortIndex(accommodation1) - getAccommodationComfortIndex(accommodation2)
  );
}

function isCharterFlight(cancellationPolicy: string): ?boolean {
  return [LEGAL_INFO_THEO_ID, LEGAL_INFO_WEBTHEO_ID].includes(cancellationPolicy);
}

function isDateAfterOptionRange(date: moment): boolean {
  return date && date.isValid() && date.diff(moment(), 'days') >= OPTIONS_DAYS_RANGE;
}

function isOptionAllowed(proposal: Proposal): boolean {
  return get(proposal, 'optionAvailable');
}

function getAdultsCount(proposal: Proposal): number {
  return get(proposal, 'adultsCount', 1);
}

function getCurrency(proposal: Proposal): ?string {
  return get(proposal, 'price.currency');
}

function getKidsCount(proposal: Proposal): number {
  return get(proposal, 'childrenCount', 0);
}

function getTotalPriceFormatted(proposal: Proposal): ?number {
  return get(proposal, 'price.total.formattedValue');
}

function getArrivalDate(proposal: Proposal): moment$Moment {
  return momentFromApiDate(get(proposal, 'arrivalDate'));
}

function getTripDeparture(proposal: Proposal): string {
  return get(proposal, 'transports.inbound.travelSections[0].departure.label', '');
}

function getTripArrival(proposal: Proposal): string {
  return get(proposal, 'transports.outbound.travelSections[0].departure.label', '');
}

function hasChildren(proposal: Proposal): boolean {
  return getKidsCount(proposal) > 0;
}

function hasChildcare(proposal: Proposal): boolean {
  return proposal.additionalServices.childcares.length > 0;
}

function hasAllInclusive(proposal: Proposal): boolean {
  return get(proposal, 'packageId') === PACKAGE_ALL_INCLUSIVE;
}

function getFormattedDate(date: string, dateFormat: string = 'DD MMMM YYYY'): string {
  return momentFromApiDate(date).format(dateFormat);
}

function getFormattedArrivalDate(proposal: Proposal, dateFormat?: string): string {
  return getFormattedDate(get(proposal, 'arrivalDate'), dateFormat);
}

function getFormattedDepartureDate(proposal: Proposal, dateFormat?: string): string {
  return getFormattedDate(get(proposal, 'departureDate'), dateFormat);
}

function getRONPackage(proposal: Proposal): ?Package {
  const packages = get(proposal, 'packages') || [];
  return find(packages, pkg => pkg.id === PACKAGE_ROOM_ONLY);
}

function getPackage(proposal: Proposal, packageId: string): ?Package {
  const packages = get(proposal, 'packages') || [];
  return find(packages, pkg => pkg.id === packageId);
}

function hasTransport(proposal: Proposal): boolean {
  return !!get(proposal, 'transports');
}

function hasTransfer(proposal: Proposal): boolean {
  return hasTransport(proposal) || !isEmpty(get(proposal, 'additionalServices.transfers'));
}

function hasTransportOrTransferSelected(proposal: Proposal): boolean {
  return (
    hasTransport(proposal) ||
    !isEmpty(
      (proposal.additionalServices.transfers || []).filter(
        transfer => transfer.pricingInfo.isFullyRented,
      ),
    )
  );
}

function isTransportATrain(proposal: Proposal): boolean {
  return (
    get(proposal, 'transports.inbound.travelSections[0].transport.type') === 'TRAIN' &&
    get(proposal, 'transports.outbound.travelSections[0].transport.type') === 'TRAIN'
  );
}

function hasAccomodation(proposal: Proposal): boolean {
  return !!get(proposal, 'accommodations.length');
}

function getResolvedProposals(proposals: Array<Proposal>): Array<Proposal> {
  return proposals.filter(p => !p.errorCode);
}

function hasErroredProposals(proposals: Array<Proposal>): boolean {
  return proposals.some(p => p.errorCode);
}

function hasFetchProposalsFailed(proposals: {
  errors: Array<*>,
  proposals: Array<Proposal>,
  dateSuggestion: Array<*>,
  suggestions: Array<*>,
}): boolean {
  return (
    (proposals.errors && proposals.errors.length > 0) ||
    hasErroredProposals(proposals.proposals) ||
    !proposals.proposals ||
    proposals.proposals.length === 0 ||
    proposals.proposals.some(proposal => proposal.price.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD)
  );
}

function getSelectedProposal(proposals: Array<Proposal>, selectedProposalIndex: number): Proposal {
  return nth(proposals, selectedProposalIndex);
}

function getSelectedProposalById(
  proposals: Array<Proposal>,
  selectedProposalId: ?string,
): ?Proposal {
  return proposals.find(proposal => proposal.id === selectedProposalId) || proposals[0];
}

function getStockWarning(
  i18n: { stockWarningLimit?: number, room?: string, rooms?: string },
  remainingStock: number,
): { showStockWarning: boolean, stockWarning: string } {
  return {
    showStockWarning: remainingStock <= get(i18n, 'stockWarningLimit', DEFAULT_STOCK_WARNING_LIMIT),
    stockWarning: `${remainingStock} ${pluralizeLabel(
      get(i18n, 'room', ''),
      get(i18n, 'rooms', ''),
      remainingStock,
    )}`,
  };
}

function getNbParticipants(proposal: Proposal): number {
  return get(proposal, 'adultsCount', 0) + get(proposal, 'childrenCount', 0);
}

function getTotalValue(proposal?: Proposal): number {
  return get(proposal, 'price.total.value', 0);
}

function getFormatedPrice(formatedPrice: string, amount: number): string {
  if (amount === 0) {
    return `${formatedPrice}`;
  }
  return amount > 0 ? `+${formatedPrice}` : `-${formatedPrice}`;
}

function getFormatedPriceByLocale(price: number, locale: string, currency: string) {
  return getFormatedPrice(formatAmountForLocale(Math.abs(price), locale, currency), price);
}

function getPriceDiff(
  proposalPrice: number,
  selectedProposalPrice: number,
  locale: string,
  currency: string,
): ?string {
  const diffPrice = proposalPrice - selectedProposalPrice;
  return diffPrice === 0 ? null : getFormatedPriceByLocale(diffPrice, locale, currency);
}

const getDefaultProposal = (proposals: $ReadOnlyArray<Proposal>, resort: ?Resort) => {
  /**
   * There is a random corner case that make selectedProposal as undefined
   * It should be the default one (mainly the first one).
   * Maybe proposals array is empty at some point...
   * Unable to reproduce the bug... See BookingEngine.container
   */
  const firstProposal = proposals[0] || {};

  if (resort && resort.id === CEFALU_RESORT) {
    const deluxeProposal = proposals.find(
      proposal => get(proposal, 'accommodations[0].comfortType') === DELUXE_COMFORT,
    );

    return deluxeProposal ? deluxeProposal : firstProposal;
  }

  return firstProposal;
};
const isPremiumAccommodation = (accommodation: Accommodation) => {
  return !!(
    accommodation.comfortId &&
    [COMFORT_TYPE_SUITE, COMFORT_TYPE_CHALET, COMFORT_TYPE_VILLA].includes(accommodation.comfortId)
  );
};
export {
  getAccommodationComfortIndex,
  getAdultsCount,
  getPackage,
  getCurrency,
  getRONPackage,
  getKidsCount,
  getFormattedArrivalDate,
  getFormattedDepartureDate,
  hasAllInclusive,
  hasChildren,
  hasChildcare,
  hasTransfer,
  getTotalPriceFormatted,
  hasTransport,
  isTransportATrain,
  isCharterFlight,
  isDateAfterOptionRange,
  isOptionAllowed,
  getArrivalDate,
  getTripArrival,
  getTripDeparture,
  hasAccomodation,
  compareAccommodationsByComfort,
  compareProposalsByComfort,
  getNbParticipants,
  hasErroredProposals,
  hasFetchProposalsFailed,
  getResolvedProposals,
  getSelectedProposal,
  getSelectedProposalById,
  getStockWarning,
  getTotalValue,
  getFormatedPrice,
  getFormatedPriceByLocale,
  getPriceDiff,
  hasTransportOrTransferSelected,
  getDefaultProposal,
  isPremiumAccommodation,
};
